/**
 * World art for the overhead course: road, roadside "Forge country" (left, charcoal and ember) and "Harvest country"
 * (right, sage fields), the fork island, encounter objects and crowds. Geometry always comes from the committed
 * course and the camera; the brand pack supplies the look (palette sampled from brand/exports/world tiles) and the
 * encounter/prop art, which is drawn when decoded with the procedural shapes below as the fallback.
 */
import { color } from '@runner/ui';
import { EncounterKind } from '@runner/simulation';
import type { CourseObjectView } from '../data/types';
import { brandImage, brandRaster, deviceScale } from './brandArt';

export interface Cam {
  W: number;
  H: number;
  cx: number; // screen x of road center
  sx: number; // px per lateral unit
  sy: number; // px per forward unit
  camY: number; // world y at the runner anchor
  anchorY: number; // screen y of the runner anchor
  sprite: number; // sprite scale
}

export const X = (c: Cam, x: number) => c.cx + x * c.sx;
export const Y = (c: Cam, y: number) => c.anchorY - (y - c.camY) * c.sy;

/**
 * Biome shades sampled from the pack's world tiles (world-forge-*, world-harvest-*). They are scenery-only mixes of
 * the brand palette with no UI-token equivalent, so they live here rather than in @runner/ui.
 */
const WORLD = {
  forgeGround: '#3E3C36', // world-forge ground field
  forgeGroundFar: '#2C2B27',
  forgeRock: '#2A2926',
  forgeRockHi: '#4A4740',
  forgeCrack: '#A75639', // ember cracks on forge ground
  forgeRoad: '#5F5D57', // forge-branch asphalt
  harvestGround: '#A6AE83', // world-harvest ground field
  harvestGroundFar: '#949F72',
  harvestRow: 'rgba(75,87,70,0.18)',
  harvestRoad: '#85847A', // harvest-branch asphalt
} as const;

const ENCOUNTER_ART = ['barrier-base', 'barrier-hit', 'burning-barrier-base', 'burning-barrier-active-overheat', 'water-station-base', 'water-station-activated', 'golden-shoe-base', 'golden-shoe-inactive', 'golden-shoe-missed', 'forge-gate-base', 'finish-base', 'finish-crossed'];
const PROPS = ['plant', 'rock', 'cone', 'fence', 'spectator'];

/** Start loading encounter and prop art (idempotent). */
export function preloadSceneArt() {
  for (const e of ENCOUNTER_ART) brandImage(`encounters/encounter-${e}.svg`);
  for (const p of PROPS) brandImage(`world/props/prop-${p}.svg`);
}

/** Pack prop (128 canvas, ground anchor [64,100]) with its anchor at (x,y), `size` CSS px; false if not loaded. */
function prop(ctx: CanvasRenderingContext2D, ds: number, name: string, x: number, y: number, size: number): boolean {
  const r = brandRaster(`world/props/prop-${name}.svg`, size * ds);
  if (!r) return false;
  ctx.drawImage(r, x - size * 0.5, y - size * (100 / 128), size, size);
  return true;
}

/** Encounter world art (256 canvas, ground anchor [128,178]) drawn `w` CSS px wide with its anchor at (x,y). */
function encounterArt(ctx: CanvasRenderingContext2D, ds: number, name: string, x: number, y: number, w: number): boolean {
  const r = brandRaster(`encounters/encounter-${name}.svg`, w * ds);
  if (!r) return false;
  ctx.drawImage(r, x - w * 0.5, y - w * (178 / 256), w, w);
  return true;
}

function encounterReady(names: string[]): boolean {
  return names.every((n) => brandImage(`encounters/encounter-${n}.svg`) !== null);
}

/** Deterministic hash → [0,1) for stable scenery placement. */
export function h01(n: number, salt = 0): number {
  let x = Math.imul(n ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(salt + 0x27d4eb2f, 0xc2b2ae35);
  x ^= x >>> 15;
  x = Math.imul(x, 0x2c1b3c6d);
  x ^= x >>> 12;
  return ((x >>> 0) % 100000) / 100000;
}

export function drawGround(ctx: CanvasRenderingContext2D, c: Cam, t: number, reduced: boolean) {
  const { W, H } = c;
  const left = X(c, -1000);
  const right = X(c, 1000);
  const ds = deviceScale(ctx);
  // Forge country (left)
  const gl = ctx.createLinearGradient(0, 0, left, 0);
  gl.addColorStop(0, WORLD.forgeGroundFar);
  gl.addColorStop(1, WORLD.forgeGround);
  ctx.fillStyle = gl;
  ctx.fillRect(0, 0, left, H);
  // Harvest country (right)
  const gr = ctx.createLinearGradient(right, 0, W, 0);
  gr.addColorStop(0, WORLD.harvestGround);
  gr.addColorStop(1, WORLD.harvestGroundFar);
  ctx.fillStyle = gr;
  ctx.fillRect(right, 0, W - right, H);

  // Tiled roadside details anchored to world y so they scroll with the course.
  const tile = 70;
  const yTop = c.camY + c.anchorY / c.sy + tile;
  const yBot = c.camY - (H - c.anchorY) / c.sy - tile;
  const i0 = Math.floor(yBot / tile);
  const i1 = Math.ceil(yTop / tile);
  const curb = curbWidth(c);
  const propSize = Math.max(22, 46 * c.sprite);
  for (let i = i0; i <= i1; i++) {
    const sy = Y(c, i * tile);
    // Forge side: charcoal rocks, ember cracks, the odd cone or fence
    const lRoom = left - curb - 4;
    if (lRoom > 8) {
      const pick = h01(i, 9);
      const px = lRoom * (0.2 + h01(i, 1) * 0.6);
      if (pick > 0.86 && lRoom > propSize * 0.9 && prop(ctx, ds, pick > 0.93 ? 'fence' : 'cone', Math.min(lRoom - propSize * 0.5, Math.max(propSize * 0.5, px)), sy, propSize * (pick > 0.93 ? 1 : 0.6))) {
        // prop drawn
      } else if (pick > 0.35) {
        const rr = (6 + h01(i, 2) * 10) * c.sprite;
        rock(ctx, px, sy, rr, h01(i, 3));
      }
      if (h01(i, 4) > 0.6) {
        const glow = reduced ? 0.75 : 0.6 + 0.3 * Math.sin(t * 2 + i);
        ctx.globalAlpha = glow;
        ctx.strokeStyle = WORLD.forgeCrack;
        ctx.lineWidth = Math.max(1.2, 2 * c.sprite);
        ctx.lineCap = 'round';
        ctx.beginPath();
        const cx0 = lRoom * h01(i, 5);
        ctx.moveTo(cx0, sy + 12 * c.sprite);
        ctx.lineTo(cx0 + 5 * c.sprite, sy + 4 * c.sprite);
        ctx.lineTo(cx0 + 2 * c.sprite, sy - 6 * c.sprite);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
    }
    // Harvest side: crop rows, plants, the odd rock or fence
    const rRoom = W - right - curb - 4;
    if (rRoom > 8) {
      const x0 = right + curb + 4;
      ctx.strokeStyle = WORLD.harvestRow;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x0 + 6, sy);
      ctx.lineTo(W, sy + 6);
      ctx.stroke();
      const pick = h01(i, 6);
      if (pick > 0.45) {
        const bx = x0 + rRoom * (0.2 + h01(i, 7) * 0.6);
        const kind = pick > 0.93 ? 'fence' : pick > 0.85 ? 'rock' : 'plant';
        const size = propSize * (kind === 'plant' ? 0.6 + h01(i, 8) * 0.4 : kind === 'rock' ? 0.7 : 1);
        const fx = Math.min(x0 + rRoom - size * 0.5, Math.max(x0 + size * 0.5, bx));
        if (rRoom < size * 0.9 || !prop(ctx, ds, kind, fx, sy, size)) {
          const br = (6 + h01(i, 8) * 8) * c.sprite;
          ctx.fillStyle = color.harvest;
          ctx.strokeStyle = color.ink;
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.arc(bx, sy, br, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        }
      }
    }
  }
}

function rock(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, k: number) {
  // Faceted ink-outlined boulder in the forge tiles' style.
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((k - 0.5) * 0.6);
  ctx.fillStyle = WORLD.forgeRock;
  ctx.strokeStyle = color.ink;
  ctx.lineWidth = Math.max(1.2, r * 0.16);
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(-r * 1.2, r * 0.3);
  ctx.lineTo(-r * 0.8, -r * 0.6);
  ctx.lineTo(r * 0.3, -r * 0.8);
  ctx.lineTo(r * 1.2, -r * 0.1);
  ctx.lineTo(r * 0.9, r * 0.7);
  ctx.lineTo(-r * 0.5, r * 0.8);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = WORLD.forgeRockHi;
  ctx.beginPath();
  ctx.moveTo(-r * 0.8, -r * 0.6);
  ctx.lineTo(r * 0.3, -r * 0.8);
  ctx.lineTo(r * 0.1, -r * 0.1);
  ctx.lineTo(-r * 0.7, r * 0.1);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

const curbWidth = (c: Cam) => Math.max(5, 8 * c.sprite);

export function drawRoad(ctx: CanvasRenderingContext2D, c: Cam, course: CourseObjectView[], showDistances = true) {
  const { H } = c;
  const left = X(c, -1000);
  const right = X(c, 1000);
  ctx.fillStyle = color.asphalt;
  ctx.fillRect(left, 0, right - left, H);
  // Low-contrast asphalt flecks (as in the world tiles), anchored to world y.
  const step = 24;
  const base = Math.floor((c.camY - (H - c.anchorY) / c.sy) / step) * step;
  for (let y = base; Y(c, y) > -20; y += step) {
    const sy = Y(c, y);
    const k = Math.floor(y / step);
    for (let j = 0; j < 3; j++) {
      ctx.fillStyle = h01(k, 30 + j) > 0.5 ? 'rgba(255,242,216,0.10)' : 'rgba(11,11,11,0.10)';
      const fx = left + (right - left) * h01(k, 11 + j);
      ctx.fillRect(fx, sy + j * 7, Math.max(1.5, 2 * c.sprite), Math.max(1.5, 2 * c.sprite));
    }
  }

  // Fork split regions
  const forks: { y0: number; y1: number }[] = [];
  for (let i = 0; i < course.length; i++) {
    if (course[i].kind !== EncounterKind.FORK) continue;
    const merge = course.slice(i).find((o) => o.kind === EncounterKind.MERGE);
    forks.push({ y0: Number(course[i].y), y1: merge ? Number(merge.y) : Number(course[i].y) + 800 });
  }
  for (const f of forks) drawBranchTint(ctx, c, f.y0, f.y1);

  // Curbs: cream band between ink lines with short orange dashes (tile boundary style).
  const curb = curbWidth(c);
  for (const [a, b] of [
    [left - curb, left],
    [right, right + curb],
  ]) {
    ctx.fillStyle = color.cream;
    ctx.fillRect(a, 0, b - a, H);
  }
  const blk = 60;
  const b0 = Math.floor((c.camY - (H - c.anchorY) / c.sy) / blk) - 1;
  ctx.fillStyle = color.orange;
  for (let k = b0; Y(c, k * blk) > -blk * c.sy; k++) {
    const y0 = Y(c, k * blk);
    const len = Math.max(4, blk * c.sy * 0.35);
    const w = Math.max(1.5, curb * 0.3);
    ctx.fillRect(left - curb / 2 - w / 2, y0 - len, w, len);
    ctx.fillRect(right + curb / 2 - w / 2, y0 - len, w, len);
  }
  ctx.strokeStyle = color.ink;
  ctx.lineWidth = Math.max(1.5, 2 * c.sprite);
  ctx.beginPath();
  for (const x of [left - curb, left, right, right + curb]) {
    ctx.moveTo(x, 0);
    ctx.lineTo(x, H);
  }
  ctx.stroke();

  // Center dashes, suppressed inside split regions
  ctx.strokeStyle = 'rgba(255,242,216,0.7)';
  ctx.lineWidth = Math.max(1.5, 2.4 * c.sprite);
  const dash = 50;
  const d0 = Math.floor((c.camY - (H - c.anchorY) / c.sy) / dash) - 1;
  for (let k = d0; Y(c, k * dash) > -dash * c.sy; k++) {
    if (k % 2) continue;
    const yy = k * dash;
    if (forks.some((f) => yy > f.y0 - 40 && yy < f.y1 + 40)) continue;
    ctx.beginPath();
    ctx.moveTo(c.cx, Y(c, yy));
    ctx.lineTo(c.cx, Y(c, yy + dash * 0.6));
    ctx.stroke();
  }

  for (const f of forks) drawSplit(ctx, c, f.y0, f.y1);

  if (!showDistances) return;
  // Distance markers every 300 units (on the dark Forge verge, so cream)
  ctx.font = `700 ${Math.max(9, 11 * c.sprite)}px ${'system-ui, sans-serif'}`;
  ctx.textAlign = 'right';
  const m0 = Math.floor((c.camY - (H - c.anchorY) / c.sy) / 300);
  for (let k = Math.max(0, m0); Y(c, k * 300) > -10; k++) {
    const sy = Y(c, k * 300);
    ctx.fillStyle = 'rgba(255,242,216,0.7)';
    ctx.fillText(`${k * 300}`, left - curb - 4, sy + 4);
  }
}

const SPLIT_MED = 140;
const SPLIT_TAPER = 160;

/** Forge (left) and Harvest (right) branches take their biome's asphalt between fork and merge. */
function drawBranchTint(ctx: CanvasRenderingContext2D, c: Cam, y0: number, y1: number) {
  const ya = Y(c, y0 + SPLIT_TAPER);
  const yb = Y(c, y1 - SPLIT_TAPER);
  for (const side of [-1, 1]) {
    const g = ctx.createLinearGradient(0, Y(c, y0), 0, ya);
    const tint = side < 0 ? WORLD.forgeRoad : WORLD.harvestRoad;
    g.addColorStop(0, color.asphalt);
    g.addColorStop(1, tint);
    ctx.fillStyle = g;
    const xa = X(c, side * 1000);
    const xb = X(c, 0);
    ctx.fillRect(Math.min(xa, xb), Y(c, y0 + SPLIT_TAPER), Math.abs(xa - xb), Y(c, y0) - ya);
    ctx.fillStyle = tint;
    ctx.fillRect(Math.min(xa, xb), yb, Math.abs(xa - xb), ya - yb);
    const g2 = ctx.createLinearGradient(0, yb, 0, Y(c, y1));
    g2.addColorStop(0, tint);
    g2.addColorStop(1, color.asphalt);
    ctx.fillStyle = g2;
    ctx.fillRect(Math.min(xa, xb), Y(c, y1), Math.abs(xa - xb), yb - Y(c, y1));
  }
}

function drawSplit(ctx: CanvasRenderingContext2D, c: Cam, y0: number, y1: number) {
  const med = SPLIT_MED;
  const taper = SPLIT_TAPER;
  const pts: [number, number][] = [
    [0, y0],
    [-med, y0 + taper],
    [-med, y1 - taper],
    [0, y1],
    [med, y1 - taper],
    [med, y0 + taper],
  ];
  // island: sand verge with a cream curb and ink edge, as in the fork / dual-route tiles
  const path = () => {
    ctx.beginPath();
    pts.forEach(([x, y], i) => (i ? ctx.lineTo(X(c, x), Y(c, y)) : ctx.moveTo(X(c, x), Y(c, y))));
    ctx.closePath();
  };
  const curb = curbWidth(c);
  path();
  ctx.lineJoin = 'round';
  ctx.strokeStyle = color.ink;
  ctx.lineWidth = curb * 2 + 4;
  ctx.stroke();
  ctx.strokeStyle = color.cream;
  ctx.lineWidth = curb * 2;
  ctx.stroke();
  ctx.fillStyle = color.sand;
  ctx.fill();
  ctx.strokeStyle = color.ink;
  ctx.lineWidth = 1.5;
  ctx.stroke();
  // a few island plants (decor only)
  const ds = deviceScale(ctx);
  const ps = Math.max(18, 34 * c.sprite);
  for (let k = 1; k <= 2; k++) {
    const yy = y0 + taper + ((y1 - y0 - 2 * taper) * k) / 3;
    if (Math.abs(X(c, med) - X(c, -med)) > ps * 1.2) prop(ctx, ds, k === 1 ? 'plant' : 'rock', c.cx, Y(c, yy), ps * (k === 1 ? 1 : 0.7));
  }
  // Signboard at the island tip
  const sy = Y(c, y0 + 60);
  const sw = Math.max(64, 92 * c.sprite);
  const sh = Math.max(20, 26 * c.sprite);
  ctx.save();
  ctx.translate(c.cx, sy);
  ctx.fillStyle = color.ink;
  ctx.fillRect(-2, 0, 4, sh * 1.2);
  for (const side of [-1, 1]) {
    ctx.save();
    ctx.translate(side * sw * 0.55, -sh * 0.3);
    ctx.rotate(side * -0.04);
    // Route markers as in the pack (prop-route-marker-left/right): ink sign with orange for Forge, cream sign for Harvest.
    ctx.fillStyle = side < 0 ? color.ink : color.cream;
    ctx.strokeStyle = color.ink;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(side * (sw / 2 + sh / 2), 0);
    ctx.lineTo(side * (sw / 2), -sh / 2);
    ctx.lineTo(-side * (sw / 2), -sh / 2);
    ctx.lineTo(-side * (sw / 2), sh / 2);
    ctx.lineTo(side * (sw / 2), sh / 2);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = side < 0 ? color.orange : color.ink;
    ctx.font = `900 ${Math.max(10, 13 * c.sprite)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(side < 0 ? '← FORGE' : 'HARVEST →', 0, 1);
    ctx.restore();
  }
  ctx.restore();
}

export interface ObjectLabel {
  title: string;
  detail: string;
  tone: string;
}

export function objectLabel(o: CourseObjectView, extras: { shoeAmount?: string; shoeStatus?: string; quoteSymbol: string }): ObjectLabel | null {
  switch (o.kind) {
    case EncounterKind.BARRIER:
      return { title: 'Barrier', detail: 'Hit = stumble, −30% pace for 2 steps', tone: color.orange };
    case EncounterKind.BURNING_BARRICADE:
      return { title: 'Burning barricade', detail: 'Hit = Overheat: run-budget slices → Forge', tone: color.forge };
    case EncounterKind.WATER_STATION:
      return { title: 'Water station', detail: 'Clears effects · run-budget slice → holders', tone: color.water };
    case EncounterKind.PUDDLE:
      return { title: 'Puddle', detail: 'Hit = −10% pace for 1 step', tone: color.water };
    case EncounterKind.GOLDEN_SHOE:
      return o.funded
        ? { title: 'Golden Shoe', detail: `${extras.shoeAmount ?? ''} ${extras.quoteSymbol} · ${extras.shoeStatus ?? 'funded'}`, tone: color.gold }
        : { title: 'Golden Shoe', detail: 'Unfunded — no prize this run', tone: color.muted };
    case EncounterKind.FORGE_GATE:
      return { title: 'Forge gate', detail: 'Pass = buyback authorization', tone: color.forge };
    case EncounterKind.FINISH:
      return { title: 'Finish', detail: 'Run complete', tone: color.ink };
    default:
      return null;
  }
}

export function drawObject(ctx: CanvasRenderingContext2D, c: Cam, o: CourseObjectView, t: number, reduced: boolean, state: { resolved?: boolean; hit?: boolean }) {
  const y = Y(c, Number(o.y));
  if (y < -200 || y > c.H + 80) return;
  const x0 = X(c, Number(o.xL));
  const x1 = X(c, Number(o.xR));
  const s = c.sprite;
  if (drawObjectArt(ctx, c, o, x0, x1, y, t, reduced, state)) return;
  switch (o.kind) {
    case EncounterKind.BARRIER: {
      const h = Math.max(8, 13 * s);
      ctx.save();
      ctx.beginPath();
      ctx.rect(x0, y - h / 2, x1 - x0, h);
      ctx.clip();
      ctx.fillStyle = color.cream;
      ctx.fillRect(x0, y - h / 2, x1 - x0, h);
      ctx.fillStyle = color.orange;
      for (let k = x0 - h; k < x1 + h; k += h * 1.4) {
        ctx.beginPath();
        ctx.moveTo(k, y + h / 2);
        ctx.lineTo(k + h * 0.7, y + h / 2);
        ctx.lineTo(k + h * 1.4, y - h / 2);
        ctx.lineTo(k + h * 0.7, y - h / 2);
        ctx.fill();
      }
      ctx.restore();
      ctx.strokeStyle = color.ink;
      ctx.lineWidth = 2;
      ctx.strokeRect(x0, y - h / 2, x1 - x0, h);
      for (const px of [x0, x1]) {
        ctx.fillStyle = color.ink;
        ctx.fillRect(px - 3, y - h, 6, h * 2);
      }
      if (state.resolved && state.hit) splinters(ctx, (x0 + x1) / 2, y, s);
      break;
    }
    case EncounterKind.BURNING_BARRICADE: {
      const h = Math.max(9, 14 * s);
      ctx.fillStyle = color.soot;
      ctx.fillRect(x0, y - h / 2, x1 - x0, h);
      ctx.strokeStyle = color.ink;
      ctx.lineWidth = 2;
      ctx.strokeRect(x0, y - h / 2, x1 - x0, h);
      // glow + flames
      const glow = ctx.createLinearGradient(0, y - h * 3, 0, y);
      glow.addColorStop(0, 'rgba(236,103,58,0)');
      glow.addColorStop(1, 'rgba(236,103,58,0.35)');
      ctx.fillStyle = glow;
      ctx.fillRect(x0, y - h * 3, x1 - x0, h * 2.6);
      const n = Math.max(4, Math.floor((x1 - x0) / (14 * s)));
      for (let k = 0; k < n; k++) {
        const fx = x0 + ((k + 0.5) / n) * (x1 - x0);
        const flick = reduced ? 0.7 : 0.6 + 0.4 * Math.sin(t * 9 + k * 1.7);
        flame(ctx, fx, y - h / 2, (10 + 8 * flick) * s, k % 3 === 0 ? color.ember : color.orange);
      }
      break;
    }
    case EncounterKind.WATER_STATION: {
      const h = Math.max(8, 12 * s);
      ctx.fillStyle = color.cream;
      ctx.fillRect(x0, y - h / 2, x1 - x0, h);
      ctx.strokeStyle = color.ink;
      ctx.lineWidth = 2;
      ctx.strokeRect(x0, y - h / 2, x1 - x0, h);
      const n = Math.max(3, Math.floor((x1 - x0) / (16 * s)));
      for (let k = 0; k < n; k++) {
        const cx = x0 + ((k + 0.5) / n) * (x1 - x0);
        ctx.fillStyle = color.water;
        ctx.beginPath();
        ctx.moveTo(cx - 4 * s, y - 2 * s);
        ctx.lineTo(cx + 4 * s, y - 2 * s);
        ctx.lineTo(cx + 3 * s, y + 5 * s);
        ctx.lineTo(cx - 3 * s, y + 5 * s);
        ctx.fill();
      }
      // banner
      ctx.fillStyle = color.water;
      ctx.fillRect(x0, y - h * 2.2, Math.min(x1 - x0, 70 * s), h);
      ctx.fillStyle = color.white;
      ctx.font = `800 ${Math.max(9, 10 * s)}px system-ui, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText('WATER', x0 + 4, y - h * 1.7);
      break;
    }
    case EncounterKind.PUDDLE: {
      // Engine v2 obstacle: a flat puddle across its lane span (no money moves).
      const cx = (x0 + x1) / 2;
      const rx = Math.max(10, (x1 - x0) / 2);
      const ry = Math.max(6, 9 * s);
      ctx.fillStyle = 'rgba(118,181,191,0.55)'; // brand water blue
      ctx.strokeStyle = color.ink;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.ellipse(cx, y, rx, ry, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.strokeStyle = 'rgba(255,242,216,0.8)';
      ctx.lineWidth = 1.5;
      const wob = reduced ? 0 : Math.sin(t * 2) * 2;
      ctx.beginPath();
      ctx.ellipse(cx - rx * 0.3, y - ry * 0.2, rx * 0.25 + wob, ry * 0.3, 0, Math.PI, Math.PI * 2);
      ctx.stroke();
      break;
    }
    case EncounterKind.GOLDEN_SHOE: {
      const cx = (x0 + x1) / 2;
      // pickup zone
      ctx.setLineDash([5, 5]);
      ctx.strokeStyle = o.funded ? color.gold : color.muted;
      ctx.lineWidth = 2;
      ctx.strokeRect(x0, y - 9 * s, x1 - x0, 18 * s);
      ctx.setLineDash([]);
      if (state.resolved && state.hit) break; // collected: the runner carries it now
      const bob = reduced ? 0 : Math.sin(t * 3) * 3 * s;
      if (o.funded && !reduced) {
        const g = ctx.createRadialGradient(cx, y - 8 * s + bob, 2, cx, y - 8 * s + bob, 30 * s);
        g.addColorStop(0, 'rgba(234,175,67,0.85)');
        g.addColorStop(1, 'rgba(234,175,67,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(cx, y - 8 * s + bob, 30 * s, 0, Math.PI * 2);
        ctx.fill();
      }
      goldenShoe(ctx, cx, y - 8 * s + bob, 1.25 * Math.max(0.8, s), o.funded);
      if (o.funded && !reduced) {
        for (let k = 0; k < 4; k++) {
          const a = t * 2 + (k * Math.PI) / 2;
          sparkle(ctx, cx + Math.cos(a) * 22 * s, y - 10 * s + Math.sin(a) * 12 * s, 3 * s);
        }
      }
      break;
    }
    case EncounterKind.FORGE_GATE: {
      const h = Math.max(30, 44 * s);
      for (const px of [x0 + 6, x1 - 6]) {
        ctx.fillStyle = color.soot;
        ctx.fillRect(px - 6 * s, y - h, 12 * s, h);
        ctx.strokeStyle = color.ink;
        ctx.lineWidth = 2;
        ctx.strokeRect(px - 6 * s, y - h, 12 * s, h);
        flame(ctx, px, y - h, 9 * s * (reduced ? 1 : 0.8 + 0.2 * Math.sin(t * 8 + px)), color.ember);
      }
      ctx.fillStyle = color.forge;
      ctx.fillRect(x0, y - h - 4, x1 - x0, 16 * Math.max(0.8, s));
      ctx.strokeStyle = color.ink;
      ctx.strokeRect(x0, y - h - 4, x1 - x0, 16 * Math.max(0.8, s));
      ctx.fillStyle = color.cream;
      ctx.font = `900 ${Math.max(9, 11 * s)}px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('FORGE GATE', (x0 + x1) / 2, y - h + 4);
      break;
    }
    case EncounterKind.FINISH: {
      const h = Math.max(10, 14 * s);
      const sq = h / 2;
      for (let k = 0; x0 + k * sq < x1; k++) {
        for (let r = 0; r < 2; r++) {
          ctx.fillStyle = (k + r) % 2 ? color.ink : color.white;
          ctx.fillRect(x0 + k * sq, y - h / 2 + r * sq, sq, sq);
        }
      }
      ctx.strokeStyle = color.ink;
      ctx.lineWidth = 2;
      ctx.strokeRect(x0, y - h / 2, x1 - x0, h);
      ctx.fillStyle = color.ink;
      ctx.font = `900 ${Math.max(12, 18 * s)}px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText('FINISH', c.cx, y - h);
      break;
    }
    default:
      break;
  }
}

/** How far (px) the decoded encounter art rises above the object's row; mirrors the sizing in drawObjectArt. */
function artAbove(c: Cam, o: CourseObjectView): number {
  const s = c.sprite;
  const span = Math.max(1, X(c, Number(o.xR)) - X(c, Number(o.xL)));
  const rise = (name: string, w: number, top: number) => (brandImage(`encounters/encounter-${name}.svg`) ? (w * (178 - top)) / 256 : 0);
  const tiledW = (vis: number, target: number) => ((span / Math.max(1, Math.round(span / target))) * 256) / vis;
  switch (o.kind) {
    case EncounterKind.BARRIER:
      return Math.max(0, rise('barrier-base', tiledW(204, 110 * s), 80) - 6 * s);
    case EncounterKind.BURNING_BARRICADE:
      return Math.max(0, rise('burning-barrier-base', tiledW(204, 110 * s), 15) - 6 * s);
    case EncounterKind.WATER_STATION:
      return rise('water-station-base', (Math.min(span, 130 * s) * 256) / 213, 46);
    case EncounterKind.GOLDEN_SHOE:
      return rise('golden-shoe-base', (Math.min(span * 0.9, 50 * Math.max(0.8, s)) * 256) / 155, 25);
    case EncounterKind.FORGE_GATE:
      return rise('forge-gate-base', (Math.min(span, 120 * s) * 256) / 210, 24);
    default:
      return 0;
  }
}

/**
 * Pack encounter art for an object, fitted to its committed lane span (labels, spans and effect text are drawn by
 * the caller/labels exactly as before). Returns false — so the procedural drawing runs — until the art has decoded,
 * and for kinds the pack has no world art for (puddle).
 */
function drawObjectArt(ctx: CanvasRenderingContext2D, c: Cam, o: CourseObjectView, x0: number, x1: number, y: number, t: number, reduced: boolean, state: { resolved?: boolean; hit?: boolean }): boolean {
  const s = c.sprite;
  const ds = deviceScale(ctx);
  const span = Math.max(1, x1 - x0);
  const cx = (x0 + x1) / 2;
  const hit = !!(state.resolved && state.hit);
  // Repeat a rail-like object across its span: n segments of ~`target` px, each art canvas sized so its visible
  // width (`vis` of 256 units) fills one segment.
  const tiled = (names: (k: number, n: number) => string, vis: number, target: number) => {
    const n = Math.max(1, Math.round(span / target));
    const seg = span / n;
    const w = (seg * 256) / vis;
    for (let k = 0; k < n; k++) encounterArt(ctx, ds, names(k, n), x0 + seg * (k + 0.5), y, w);
  };
  // Lane-span strip on the road for objects whose art is narrower than the span they cover.
  const spanStrip = (tone: string) => {
    const h = Math.max(4, 6 * s);
    ctx.globalAlpha = 0.45;
    ctx.fillStyle = tone;
    ctx.fillRect(x0, y - h / 2, span, h);
    ctx.globalAlpha = 1;
    ctx.fillStyle = color.ink;
    ctx.fillRect(x0 - 1.5, y - h, 3, h * 2);
    ctx.fillRect(x1 - 1.5, y - h, 3, h * 2);
  };
  switch (o.kind) {
    case EncounterKind.BARRIER: {
      const name = hit ? 'barrier-hit' : 'barrier-base';
      if (!encounterReady([name])) return false;
      tiled(() => name, 204, 110 * s);
      return true;
    }
    case EncounterKind.BURNING_BARRICADE: {
      const name = hit ? 'burning-barrier-active-overheat' : 'burning-barrier-base';
      if (!encounterReady([name])) return false;
      tiled(() => name, 204, 110 * s);
      return true;
    }
    case EncounterKind.WATER_STATION: {
      const name = hit ? 'water-station-activated' : 'water-station-base';
      if (!encounterReady([name])) return false;
      spanStrip(color.water);
      const vis = Math.min(span, 130 * s);
      encounterArt(ctx, ds, name, cx, y, (vis * 256) / 213);
      return true;
    }
    case EncounterKind.GOLDEN_SHOE: {
      const missed = state.resolved && !state.hit;
      const name = !o.funded ? 'golden-shoe-inactive' : missed ? 'golden-shoe-missed' : 'golden-shoe-base';
      if (!encounterReady([name])) return false;
      // pickup zone
      ctx.setLineDash([5, 5]);
      ctx.strokeStyle = o.funded ? color.gold : color.muted;
      ctx.lineWidth = 2;
      ctx.strokeRect(x0, y - 9 * s, span, 18 * s);
      ctx.setLineDash([]);
      if (hit) return true; // collected: the runner's pickup clip acknowledges it; no shoe left on the road
      const bob = reduced || missed ? 0 : Math.sin(t * 3) * 3 * s;
      const vis = Math.min(span * 0.9, 50 * Math.max(0.8, s));
      const w = (vis * 256) / 155;
      if (o.funded && !reduced && !missed) {
        const gy = y - w * 0.3 + bob;
        const g = ctx.createRadialGradient(cx, gy, 2, cx, gy, w * 0.45);
        g.addColorStop(0, 'rgba(234,175,67,0.75)');
        g.addColorStop(1, 'rgba(234,175,67,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(cx, gy, w * 0.45, 0, Math.PI * 2);
        ctx.fill();
      }
      encounterArt(ctx, ds, name, cx, y + bob, w);
      if (o.funded && !reduced && !missed) {
        for (let k = 0; k < 4; k++) {
          const a = t * 2 + (k * Math.PI) / 2;
          sparkle(ctx, cx + Math.cos(a) * 26 * s, y - 14 * s + Math.sin(a) * 12 * s, 3 * s);
        }
      }
      return true;
    }
    case EncounterKind.FORGE_GATE: {
      // No financial stage is shown on the gate: authorization/purchase/burn are verified elsewhere and labelled in UI.
      if (!encounterReady(['forge-gate-base'])) return false;
      spanStrip(color.ember);
      const vis = Math.min(span, 120 * s);
      encounterArt(ctx, ds, 'forge-gate-base', cx, y, (vis * 256) / 210);
      return true;
    }
    case EncounterKind.FINISH: {
      if (!encounterReady(['finish-base', 'finish-crossed'])) return false;
      // checkered ground line across the full span, ribbons on posts above it
      const h = Math.max(8, 12 * s);
      const sq = h / 2;
      for (let k = 0; x0 + k * sq < x1; k++) {
        for (let r = 0; r < 2; r++) {
          ctx.fillStyle = (k + r) % 2 ? color.ink : color.cream;
          ctx.fillRect(x0 + k * sq, y - h / 2 + r * sq, Math.min(sq, x1 - (x0 + k * sq)), sq);
        }
      }
      const n = Math.max(1, Math.round(span / (170 * s)));
      const mid = Math.floor(n / 2);
      tiled((k) => (state.resolved && k === mid ? 'finish-crossed' : 'finish-base'), 232, 170 * s);
      const seg = span / n;
      const w = (seg * 256) / 232;
      ctx.fillStyle = color.ink;
      ctx.font = `900 ${Math.max(12, 18 * s)}px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText('FINISH', c.cx, y - w * ((178 - 40) / 256));
      return true;
    }
    default:
      return false;
  }
}

export interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** How to clear the box: lift the label above it (runner) or move it to its left (overlay controls). */
  clear: 'up' | 'left';
}

export function drawLabel(ctx: CanvasRenderingContext2D, c: Cam, o: CourseObjectView, lab: ObjectLabel, side: 1 | -1, avoid: Box[] = []) {
  const y = Y(c, Number(o.y));
  if (y < 10 || y > c.H - 10) return;
  // Legibility floor: never below 11px, and the detail line wraps instead of shrinking on narrow canvases.
  const fs = Math.max(11, Math.min(13, 12 * c.sprite));
  const fd = Math.max(11, fs - 1);
  const maxW = Math.min(c.W - 8, 300);
  ctx.font = `800 ${fs}px system-ui, sans-serif`;
  const w1 = ctx.measureText(lab.title).width;
  ctx.font = `500 ${fd}px system-ui, sans-serif`;
  const lines = wrapText(ctx, lab.detail, maxW - 14);
  const w2 = Math.max(...lines.map((l) => ctx.measureText(l).width));
  const w = Math.min(maxW, Math.max(w1, w2) + 14);
  const h = fs + lines.length * (fd + 2) + 10;
  // Prefer the requested side; never cover the object itself: use the open side, or sit above a full-width object.
  const rightX = X(c, Number(o.xR)) + 10;
  const leftX = X(c, Number(o.xL)) - 10 - w;
  const fitsRight = rightX + w <= c.W - 4;
  const fitsLeft = leftX >= 4;
  let x: number;
  let yy = y - h / 2 - 16 * c.sprite;
  if (side > 0 ? fitsRight : fitsLeft) x = side > 0 ? rightX : leftX;
  else if (fitsRight) x = rightX;
  else if (fitsLeft) x = leftX;
  else {
    x = Math.max(4, Math.min(c.W - w - 4, (X(c, Number(o.xL)) + X(c, Number(o.xR))) / 2 - w / 2));
    yy = y - h - 22 * c.sprite - artAbove(c, o);
  }
  // Never cover the runner (lift the label clear of its sprite) or sit under the overlay controls (move it left).
  for (const b of avoid) {
    if (x < b.x1 && x + w > b.x0 && yy < b.y1 && yy + h > b.y0) {
      if (b.clear === 'up') yy = b.y0 - h - 6;
      else x = Math.max(4, b.x0 - w - 6);
    }
  }
  if (yy < 2) return;
  ctx.fillStyle = 'rgba(255,242,216,0.95)';
  ctx.strokeStyle = color.ink;
  ctx.lineWidth = 1.5;
  roundRect(ctx, x, yy, w, h, 6);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = lab.tone;
  ctx.fillRect(x, yy + 4, 4, h - 8);
  ctx.fillStyle = color.ink;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.font = `800 ${fs}px system-ui, sans-serif`;
  ctx.fillText(lab.title, x + 9, yy + 5);
  ctx.fillStyle = color.inkSoft;
  ctx.font = `500 ${fd}px system-ui, sans-serif`;
  lines.forEach((l, i) => ctx.fillText(l, x + 9, yy + 7 + fs + i * (fd + 2)));
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, max: number): string[] {
  if (ctx.measureText(text).width <= max) return [text];
  const words = text.split(' ');
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const next = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(next).width > max && cur) {
      lines.push(cur);
      cur = w;
    } else cur = next;
  }
  if (cur) lines.push(cur);
  return lines.slice(0, 3);
}

export function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export function flame(ctx: CanvasRenderingContext2D, x: number, y: number, h: number, fill: string) {
  ctx.fillStyle = fill;
  ctx.strokeStyle = color.ink;
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(x - h * 0.35, y);
  ctx.quadraticCurveTo(x - h * 0.45, y - h * 0.55, x, y - h);
  ctx.quadraticCurveTo(x + h * 0.45, y - h * 0.55, x + h * 0.35, y);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = color.goldSoft;
  ctx.beginPath();
  ctx.moveTo(x - h * 0.15, y);
  ctx.quadraticCurveTo(x - h * 0.18, y - h * 0.3, x, y - h * 0.55);
  ctx.quadraticCurveTo(x + h * 0.18, y - h * 0.3, x + h * 0.15, y);
  ctx.fill();
}

export function goldenShoe(ctx: CanvasRenderingContext2D, x: number, y: number, s: number, funded: boolean) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(s, s);
  ctx.fillStyle = funded ? color.gold : color.sand;
  ctx.strokeStyle = color.ink;
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.moveTo(-11, 3);
  ctx.bezierCurveTo(-11, -5, -3, -8, 3, -6);
  ctx.bezierCurveTo(8, -5, 12, -1, 13, 3);
  ctx.lineTo(13, 6);
  ctx.lineTo(-11, 6);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = funded ? color.orangeDeep : color.muted;
  ctx.fillRect(-11, 4, 24, 3.5);
  ctx.strokeRect(-11, 4, 24, 3.5);
  ctx.strokeStyle = color.ink;
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(-2, -5);
  ctx.lineTo(1, -1);
  ctx.moveTo(2, -5.5);
  ctx.lineTo(5, -1.5);
  ctx.stroke();
  ctx.restore();
}

export function sparkle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number) {
  ctx.fillStyle = color.white;
  ctx.beginPath();
  ctx.moveTo(x, y - r * 2);
  ctx.lineTo(x + r * 0.5, y - r * 0.5);
  ctx.lineTo(x + r * 2, y);
  ctx.lineTo(x + r * 0.5, y + r * 0.5);
  ctx.lineTo(x, y + r * 2);
  ctx.lineTo(x - r * 0.5, y + r * 0.5);
  ctx.lineTo(x - r * 2, y);
  ctx.lineTo(x - r * 0.5, y - r * 0.5);
  ctx.closePath();
  ctx.fill();
}

function splinters(ctx: CanvasRenderingContext2D, x: number, y: number, s: number) {
  ctx.strokeStyle = color.ink;
  ctx.lineWidth = 1.5;
  for (let k = 0; k < 5; k++) {
    const a = (k / 5) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(x + Math.cos(a) * 6 * s, y + Math.sin(a) * 4 * s);
    ctx.lineTo(x + Math.cos(a) * 14 * s, y + Math.sin(a) * 9 * s);
    ctx.stroke();
  }
}

/** Spectators lining the course; they cheer as RUNNER passes. Deterministic placement per world tile. */
export function drawCrowd(ctx: CanvasRenderingContext2D, c: Cam, t: number, reduced: boolean, runnerY: number) {
  const left = X(c, -1000) - 12 * c.sprite;
  const right = X(c, 1000) + 12 * c.sprite;
  const tile = 45;
  const i0 = Math.floor((c.camY - (c.H - c.anchorY) / c.sy) / tile) - 1;
  const i1 = Math.ceil((c.camY + c.anchorY / c.sy) / tile) + 1;
  const s = c.sprite;
  const ds = deviceScale(ctx);
  for (let i = i0; i <= i1; i++) {
    const wy = i * tile;
    const sy = Y(c, wy);
    for (const side of [-1, 1]) {
      // Sparser than the old dot crowd: the pack spectators are full figures and share the verge with props.
      if (h01(i, side > 0 ? 21 : 22) < 0.84) continue;
      const room = side < 0 ? left : c.W - right;
      if (room < 14) continue;
      const depth = h01(i, side > 0 ? 23 : 24);
      const half = Math.max(8, 15 * s);
      const reach = Math.max(0, Math.min(room - 12 - half, 60 * s));
      const x = side < 0 ? left - 8 * s - half * 0.6 - depth * reach : right + 8 * s + half * 0.6 + depth * reach;
      const near = Math.max(0, 1 - Math.abs(wy - runnerY) / 260);
      const cheer = reduced ? 0 : near * Math.abs(Math.sin(t * 7 + i));
      // Pack spectator prop (an ink creature in a cream singlet); bobs while cheering RUNNER past.
      if (!prop(ctx, ds, 'spectator', x, sy + 4 * s - cheer * 3 * s, Math.max(16, 30 * s))) {
        const shirt = [color.orange, color.cream, color.ink, color.harvest, color.gold][Math.floor(h01(i, side + 30) * 5)];
        ctx.fillStyle = shirt;
        ctx.strokeStyle = color.ink;
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.ellipse(x, sy + 3 * s, 6 * s, 4.5 * s, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = color.ink; // spectators are ink creatures like RUNNER, never skin tones
        ctx.beginPath();
        ctx.arc(x, sy - 3 * s - cheer * 3 * s, 3.6 * s, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        if (cheer > 0.25 || h01(i, side + 50) > 0.8) {
          ctx.strokeStyle = color.ink;
          ctx.lineWidth = 1.6 * s;
          ctx.beginPath();
          ctx.moveTo(x - 4 * s, sy);
          ctx.lineTo(x - 7 * s, sy - 7 * s - cheer * 4 * s);
          ctx.moveTo(x + 4 * s, sy);
          ctx.lineTo(x + 7 * s, sy - 7 * s - cheer * 4 * s);
          ctx.stroke();
        }
      }
      if (h01(i, side + 60) > 0.86) {
        // flag
        const fx = x + side * -6 * s;
        ctx.strokeStyle = color.ink;
        ctx.beginPath();
        ctx.moveTo(fx, sy);
        ctx.lineTo(fx, sy - 18 * s);
        ctx.stroke();
        ctx.fillStyle = side < 0 ? color.forge : color.harvest;
        ctx.beginPath();
        const wave = reduced ? 0 : Math.sin(t * 5 + i) * 2 * s;
        ctx.moveTo(fx, sy - 18 * s);
        ctx.lineTo(fx + side * -12 * s, sy - 15 * s + wave);
        ctx.lineTo(fx, sy - 11 * s);
        ctx.fill();
      }
    }
  }
}
